<template>
	<div class="import-wizard">
		<!-- Step 1: Select blueprint -->
		<div class="step-card" :class="{ 'step-complete': step1Complete }">
			<div class="step-header">
				<div class="step-number">1</div>
				<div class="step-title">Select blueprint</div>
			</div>
			<div style="min-width:280px; flex: 1;">
				<label style="display:block; font-weight:500; margin-bottom:6px;">Select which blueprint type to import</label>
				<DxSelectBox :items="schemaOptions" value-expr="id" display-expr="label" :search-enabled="true" :search-mode="'contains'" :show-clear-button="true" v-model="selectedSchemaId" />
			</div>
		</div>

		<!-- Step 2: Configure options -->
		<div class="step-card" :class="{ 'step-complete': step2Complete }" @click="step2Changed = true">
			<div class="step-header">
				<div class="step-number">2</div>
				<div class="step-title">CSV import options</div>
			</div>
			<div style="min-width:180px; padding: 20px 0;">
				<DxCheckBox :value="firstRowIsHeader" @value-changed="firstRowIsHeader = $event.value" text="First row contains headers" /><br>
				<DxCheckBox :value="ignoreEmptyRows" @value-changed="ignoreEmptyRows = $event.value" text="Ignore empty rows" style="margin-top:8px;" />
				<DxCheckBox :value="ignoreEmptyRelations" @value-changed="ignoreEmptyRelations = $event.value" text="Ignore empty relationships" style="margin-top:8px;margin-left:16px;" />
				<div style="margin-top:12px; min-width:220px;">
					<label style="display:block; font-weight:500; margin-bottom:6px;">Delimiter</label>
					<DxSelectBox
						:items="delimiterOptions"
						value-expr="value"
						display-expr="text"
						v-model="delimiterMode"
					/>
				</div>
			</div>
		</div>

		<!-- Step 3: Upload CSV -->
		<div class="step-card" :class="{ 'step-complete': step3Complete }">
			<div class="step-header">
				<div class="step-number">3</div>
				<div class="step-title">Upload CSV</div>
			</div>
			<div style="display:flex; align-items:center; gap: 12px;">
				<div style="min-width:260px;">
					<DxFileUploader :multiple="false" upload-mode="useForm" :accept="'.csv,text/csv'" select-button-text="Choose CSV" label-text="" @value-changed="onFileChosen" />
				</div>
				<div style="margin-left:auto; display:flex; gap:8px;">
					<DxButton type="normal" icon="refresh" :disabled="!rawCsvText" text="Re-parse" @click="reparseCsv" />
				</div>
			</div>
		</div>

		<!-- Step 4: Preview and mapping -->
		<div class="step-card" :class="{ 'step-complete': step4Complete }">
			<div class="step-header">
				<div class="step-number">4</div>
				<div class="step-title">Preview and mapping</div>
			</div>
			<div v-if="csvColumns.length" :style="isCompact ? 'display:flex; flex-direction:column; gap:12px; align-items:stretch;' : 'display:flex; gap:16px; align-items:flex-start;'">
				<div :style="isCompact ? 'width:100%; min-width:0;' : 'flex:2; min-width:420px;'">
					<div style="font-weight:600; margin: 10px 0 6px;">Preview (first 10 rows) - right click on headers to map columns</div>
					<DxDataGrid :data-source="previewRows" :columns="previewColumns" :show-borders="true" :row-alternation-enabled="true" :allowColumnResizing="true" columnResizingMode="widget" :columnAutoWidth="true" :columnMinWidth="120" height="320px" @context-menu-preparing="onPreviewContextMenu">
						<DxScrolling :useNative="true" />
						<DxSorting mode="none" />
					</DxDataGrid>
				</div>
				<div :style="isCompact ? 'width:100%; min-width:0;' : 'flex:1; min-width:255px;'">
					<div style="display:flex; align-items:center; gap:8px;">
						<div style="font-weight:600; margin: 10px 0 6px;">Column mapping</div>
						<DxButton type="normal" text="Auto map" :disabled="!selectedSchema || !csvColumns.length" @click="autoMap" />
						<DxButton type="normal" text="Clear all" :disabled="!selectedSchema || !csvColumns.length" @click="clearMap" />
					</div>
            <div :style="isCompact ? 'display:flex; flex-direction:column; gap:8px; padding-right:0;' : 'display:flex; flex-direction:column; gap:8px; max-height:320px; overflow:auto; padding-right:6px;'">
                <div v-for="(col, idx) in csvColumns" :key="'map-'+idx" style="display:grid; grid-template-columns: 1fr; gap:4px;">
                    <div style="font-size:12px; color: var(--dx-color-text-muted);">{{ col }}</div>
                    <DxDropDownBox :ref="el => registerMappingDDBRef(idx, el)" v-model:value="columnMapping[idx]" :data-source="fieldOptions" value-expr="value" display-expr="text" :show-clear-button="true" :defer-rendering="false" placeholder="Ignore" :display-value-formatter="formatMappingValue" content-template="mappingTree">
                        <template #mappingTree>
                            <div class="mapping-tree">
                                <DxTreeView :items="mappingTreeItems" :select-by-click="false" selection-mode="none" expand-event="click" :focus-state-enabled="false" :hover-state-enabled="false" :active-state-enabled="false" @item-click="(e) => onMappingTreeItemClick(e, idx)" />
                            </div>
                        </template>
                    </DxDropDownBox>
                </div>
            </div>
				<div v-if="duplicateMappingInfo.length" style="margin-top:6px; padding:8px; border:1px solid rgba(244, 67, 54, 0.35); border-radius:6px; background: rgba(244, 67, 54, 0.06); color: var(--dx-color-danger);">
					<div style="font-weight:600; margin-bottom:4px;">Warning: Multiple columns map to the same field</div>
					<div v-for="dup in duplicateMappingInfo" :key="dup.target" style="font-size:12px;">
						{{ dup.target }} - maps to: {{ dup.cols.join(', ') }}
					</div>
				</div>
				</div>
			</div>
			<div v-else style="padding: 12px; color: var(--dx-color-text-muted);">
				No columns to preview yet. Upload a CSV in step 3 to see a preview and map columns.
			</div>
		</div>

		<!-- Step 5: Status and Import -->
		<div class="step-card">
			<div class="step-header">
				<div class="step-number">5</div>
				<div class="step-title">Status and import</div>
			</div>
			<div style="display:flex; flex-direction:column; gap:4px;">
				<div>Blueprint: <span style="font-weight:500;">{{ schemaLabel || '—' }}</span></div>
				<div>Rows detected: <span style="font-weight:500;">{{ rowsAsObjects.length }}</span></div>
				<div>Mapped columns: <span style="font-weight:500;">{{ mappedCount }} / {{ csvColumns.length }}</span></div>
				<div v-if="!canImport" style="color: var(--dx-color-danger);">{{ importHint }}</div>
			</div>
			<div style="margin-top:10px; display:flex; align-items:center; gap:10px; justify-content:flex-end;">
				<i :class="importUnlocked ? 'dx-icon-unlock' : 'dx-icon-lock'"></i>
				<DxSwitch v-model:value="importUnlocked" switched-on-text="Unlocked" switched-off-text="Locked" :disabled="isImporting" />
				<div :class="{ 'import-locked': !importUnlocked }">
					<DxDropDownButton type="success" styling-mode="contained" icon="check" :split-button="true" :items="importMenuItems"
						:disabled="!canImport || isImporting" :hint="importHint"
						:text="isImporting ? 'Running Import…' : ('Run Import (' + rowsAsObjects.length + ' rows)')"
						@button-click="runImport" @item-click="onImportMenuClick"
					/>
				</div>
			</div>
		</div>
		<DxPopup
			:visible="showSimPopup"
			:show-close-button="true"
			:drag-enabled="true"
			:hide-on-outside-click="true"
			:title="'Import simulation results'"
			:width="isCompact ? '95vw' : '900px'"
			:height="isCompact ? '80vh' : '70vh'"
			@hiding="showSimPopup = false"
		>
			<div style="display:flex; align-items:center; gap:8px; margin-bottom:8px;">
				<div style="font-weight:600;">Would create {{ simulateItemCount }} item(s) and {{ simulateChildrenCount }} children</div>
				<div style="margin-left:auto; display:flex; gap:8px;">
					<DxButton type="normal" icon="copy" text="Copy JSON" @click="copySimJson" />
				</div>
			</div>
			<div style="height:calc(100% - 40px); overflow:auto;">
				<pre class="json-viewer">{{ simulateJsonPretty }}</pre>
			</div>
		</DxPopup>
	</div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, inject, onMounted, onUnmounted, computed } from 'vue';
import DxDataGrid, { DxScrolling, DxSorting } from 'devextreme-vue/data-grid';
import DxFileUploader from 'devextreme-vue/file-uploader';
import DxSelectBox from 'devextreme-vue/select-box';
import DxCheckBox from 'devextreme-vue/check-box';
import DxButton from 'devextreme-vue/button';
import DxSwitch from 'devextreme-vue/switch';
import { DxDropDownButton } from 'devextreme-vue/drop-down-button';
import DxDropDownBox from 'devextreme-vue/drop-down-box';
import DxPopup from 'devextreme-vue/popup';
// Keep refs to each mapping dropdown to programmatically close
const mappingDDBRefs = ref({});
function registerMappingDDBRef(idx, el) {
	if (!el) return;
	mappingDDBRefs.value[idx] = el;
}
import DxTreeView from 'devextreme-vue/tree-view';
import { SchemaCache } from '@/schemas.js';
import { alert as dxAlert } from 'devextreme/ui/dialog';
import { sizes as getSizes, subscribe as subscribeMedia, unsubscribe as unsubscribeMedia } from '@/utils/media-query.js';

const $http = inject('$http');
const props = defineProps({
	context: { type: Object, default: () => ({}) },
	componentSettings: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['completed']);

const selectedSchemaId = ref(null);
const selectedSchema = computed(() => selectedSchemaId.value ? SchemaCache.getSchemaById(selectedSchemaId.value) : null);
const schemaLabel = computed(() => {
	const s = selectedSchema.value;
	return s ? (s.label || s.singular || s.title || s.type || s.keyspec || ('#' + s.id)) : '';
});
const firstRowIsHeader = ref(true);
const ignoreEmptyRows = ref(true);
const ignoreEmptyRelations = ref(true);
const isImporting = ref(false);
const importUnlocked = ref(false);

// Import dropdown items and handlers
const importMenuItems = [
	{ id: 'simulate', text: 'Simulate import', icon: 'runner' },
	{ id: 'dump_payloads', text: 'Dump payloads only', icon: 'orderedlist' },
];
function onImportMenuClick(e) {
	const id = e?.itemData?.id;
	if (id === 'dump_payloads' || id === 'simulate')
		runImport(id === 'simulate', id === 'dump_payloads');
}

// Responsive handling (popup context; no global app classes here)
const screen = ref(getSizes());
const isCompact = computed(() => !!screen.value['screen-x-small'] || !!screen.value['screen-small']);
let mediaHandler = null;

// Delimiter selection
const delimiterMode = ref('guess'); // 'guess' | ',' | ';' | 'tab'
const delimiterOptions = ref([
	{ value: 'guess', text: 'Guess' },
	{ value: ',', text: 'Comma (,)' },
	{ value: ';', text: 'Semicolon (;)' },
	{ value: 'tab', text: 'Tab (↹)' },
]);

const schemaOptions = ref([]);
const fieldOptions = computed(() => {
	if (!selectedSchema.value) return [];
	const options = [];
	for (const f of (selectedSchema.value.fields || [])) {
		if (f.field === 'id') continue;
		const baseLabel = f.label || f.field;
		// Top-level field option
		options.push({ value: f.field, text: baseLabel });
		// For relationship fields, also expose sub-field mapping options
		if (f.fieldtype === 'relationship') {
			// Always allow mapping to id directly
			options.push({ value: f.field + '.id', text: baseLabel + ' › id' });
			const rel = f.getRelationshipDetails?.();
			if (rel && Array.isArray(rel.oppositeSchemas)) {
				for (const oppSchema of rel.oppositeSchemas) {
					const oppLabel = oppSchema.label || oppSchema.singular || oppSchema.title || oppSchema.type || oppSchema.keyspec || '';
					for (const sf of (oppSchema.fields || [])) {
						if (sf.field === 'id') continue;
						const subText = oppLabel ? `${baseLabel} › ${oppLabel} › ${sf.label || sf.field}` : `${baseLabel} › ${sf.label || sf.field}`;
						options.push({ value: f.field + '.' + sf.field, text: subText });
					}
				}
			}
		}
	}
	return options;
});

// Map for quick label lookup
const fieldLabelMap = computed(() => Object.fromEntries(fieldOptions.value.map(o => [o.value, o.text])));

// Tree items for dropdown mapping selector
const mappingTreeItems = computed(() => {
	if (!selectedSchema.value) return [];
	const items = [];
	for (const f of (selectedSchema.value.fields || [])) {
		if (f.field === 'id') continue;
		if (f.fieldtype !== 'relationship') {
			items.push({ id: f.field, text: f.label || f.field, value: f.field });
			continue;
		}
		const relChildren = [];
		// id option
		relChildren.push({ id: f.field + '.id', text: 'id', value: f.field });
		const rel = f.getRelationshipDetails?.();
		if (rel && Array.isArray(rel.oppositeSchemas) && rel.oppositeSchemas.length > 0) {
			if (rel.oppositeSchemas.length > 1) {
				for (const opp of rel.oppositeSchemas) {
					const oppLabel = opp.label || opp.singular || opp.title || opp.type || opp.keyspec || 'Related';
					const oppChildren = [];
					for (const sf of (opp.fields || [])) {
						if (sf.field === 'id') continue;
						oppChildren.push({ id: f.field + '.' + sf.field, text: sf.label || sf.field, value: f.field + '.' + sf.field });
					}
					if (oppChildren.length) relChildren.push({ id: f.field + ':' + (opp.type || oppLabel), text: oppLabel, items: oppChildren });
				}
			} else {
				const opp = rel.oppositeSchemas[0];
				for (const sf of (opp.fields || [])) {
					if (sf.field === 'id') continue;
					relChildren.push({ id: f.field + '.' + sf.field, text: sf.label || sf.field, value: f.field + '.' + sf.field });
				}
			}
		}
		items.push({ id: f.field, text: f.label || f.field, items: relChildren });
	}
	return items;
});

function formatMappingValue(value) {
	if (!value) return '';
	// Normalize relationship base mapping to show as "label › id"
	if (value && typeof value === 'string' && !value.includes('.')) {
		// Try relationship label
		const label = fieldLabelMap.value[value + '.id'] || fieldLabelMap.value[value] || value;
		return label;
	}
	return fieldLabelMap.value[value] || value;
}

function onMappingTreeItemClick(e, idx) {
	const node = e?.itemData;
	if (!node) return;
	// Only commit when node has a value (leaf or id option)
	if (!('value' in node)) return;
	const updated = { ...columnMapping.value };
	updated[idx] = node.value;
	columnMapping.value = updated;
	try {
		const el = mappingDDBRefs.value[idx];
		const inst = el?.instance || el;
		inst?.option?.('opened', false);
	} catch (_) { /* ignore close errors */ }
}

// Step completion tracking
const step2Changed = ref(false); // latched on click anywhere in step 2 card
const step1Complete = computed(() => !!selectedSchema.value);
const step2Complete = computed(() => step2Changed.value);
const step3Complete = computed(() => !!rawCsvText.value);
const step4Complete = computed(() => mappedCount.value > 0);

// CSV parsing + preview
const rawCsvText = ref('');
const csvColumns = ref([]); // array of column names
const csvRows = ref([]); // array of rows: array of values aligned to columns
const previewRows = computed(() => rowsAsObjects.value.slice(0, 10));
const previewColumns = computed(() => csvColumns.value.map((c, i) => {
	const dataFieldKey = toDataFieldKey(c);
	const mappedField = columnMapping.value[i];
	if (mappedField && selectedSchema.value) {
		let captionLabel = mappedField;
		if (String(mappedField).includes('.')) {
			const [parentKey, subKey] = String(mappedField).split('.');
			const parentFld = selectedSchema.value.fields.find(f => f.field === parentKey);
			const parentLabel = parentFld ? (parentFld.label || parentFld.field) : parentKey;
			let subLabel = subKey;
			if (parentFld && parentFld.fieldtype === 'relationship') {
				if (subKey === 'id') {
					subLabel = 'id';
				} else {
					const rel = parentFld.getRelationshipDetails?.();
					if (rel) {
						for (const opp of rel.oppositeSchemas || []) {
							const sf = (opp.fields || []).find(x => x.field === subKey);
							if (sf) { subLabel = sf.label || sf.field; break; }
						}
					}
				}
			}
			captionLabel = `${parentLabel}.${subLabel}`;
		} else {
			const fld = selectedSchema.value.fields.find(f => f.field === mappedField);
			let mappedLabel = fld ? (fld.label || fld.field) : mappedField;
			if (fld && fld.fieldtype === 'relationship') mappedLabel += ' (id)';
			captionLabel = mappedLabel;
		}
		return { name: c, dataField: dataFieldKey, caption: `${c} => ${captionLabel}`, calculateCellValue: (data) => data[c] };
	}
	return { name: c, dataField: dataFieldKey, caption: c, calculateCellValue: (data) => data[c] };
}));
const columnMapping = ref({}); // index -> field.field or undefined
const mappedCount = computed(() => Object.values(columnMapping.value).filter(Boolean).length);
const rowsAsObjects = computed(() => {
	if (!csvColumns.value.length) return [];
	return csvRows.value.map(rowArr => Object.fromEntries(csvColumns.value.map((c, i) => [c, rowArr[i] ?? null])));
});

onMounted(async () => {
	await SchemaCache.waitForData();
	const schemas = SchemaCache.getAllSchemasExceptRelations();
	schemaOptions.value = schemas.map(s => ({
		id: s.id,
		label: s.label || s.singular || s.title || s.type || s.keyspec || ('#' + s.id),
	}));

	// Auto-select default schema from componentSettings.source if provided
	const source = props.componentSettings?.source;
	if (typeof source === 'string' && source.trim()) {
		const match = SchemaCache.getSchemaByType(source.trim());
		if (match) {
			selectedSchemaId.value = match.id;
		}
	}

	// Subscribe to media changes locally
	mediaHandler = () => { screen.value = getSizes(); };
	subscribeMedia(mediaHandler);

	// Reset the changed flag for step 2 when opening
	step2Changed.value = false;
});

onUnmounted(() => {
	if (mediaHandler) unsubscribeMedia(mediaHandler);
});

watch(selectedSchemaId, () => {
	// Reset mapping when schema changes
	columnMapping.value = {};
});

watch(firstRowIsHeader, () => {
    // Re-parse CSV when header mode toggles
    if (!rawCsvText.value) return;
    columnMapping.value = {};
    parseAndPrepare(rawCsvText.value);
});

watch(delimiterMode, () => {
    // Re-parse CSV when delimiter changes
    if (!rawCsvText.value) return;
    columnMapping.value = {};
    parseAndPrepare(rawCsvText.value);
});

watch(ignoreEmptyRows, () => {
	// Re-parse CSV when empty-row preference changes (does not affect headers)
	if (!rawCsvText.value) return;
	parseAndPrepare(rawCsvText.value);
});

function onFileChosen(e) {
	const files = e.value || [];
	if (!files.length) return;
	const file = files[0];
	const reader = new FileReader();
	reader.onload = () => {
		rawCsvText.value = String(reader.result || '');
		parseAndPrepare(rawCsvText.value);
	};
	reader.readAsText(file);
}

function reparseCsv() {
	if (!rawCsvText.value) return;
	parseAndPrepare(rawCsvText.value);
}

function parseAndPrepare(text) {
	const { delimiter, rows } = parseCsv(text);
    console.log('Parsed CSV', { delimiter, rows });
	// Determine headers
	let headers = [];
	let dataRows = [];
	if (rows.length === 0) {
		csvColumns.value = [];
		csvRows.value = [];
		return;
	}
	if (firstRowIsHeader.value) {
		headers = rows[0].map((h, i) => sanitizeHeader(String(h || '').trim() || `Column ${i + 1}`));
		dataRows = rows.slice(1);
		// If data rows have more columns than the header row, extend headers to avoid truncation
		const dataMaxLen = dataRows.length ? Math.max(...dataRows.map(r => r.length)) : headers.length;
		if (dataMaxLen > headers.length) {
			for (let i = headers.length; i < dataMaxLen; i++) {
				headers.push(`Column ${i + 1}`);
			}
		}
	} else {
		const maxLen = Math.max(...rows.map(r => r.length));
		headers = Array.from({ length: maxLen }, (_, i) => `Column ${i + 1}`);
		dataRows = rows;
	}
	// Optionally drop completely empty rows (all cells empty/whitespace) from data rows
	if (ignoreEmptyRows.value) {
		dataRows = dataRows.filter(r => {
			const norm = normalizeRowLength(r, headers.length);
			return norm.some(cell => String(cell ?? '').trim() !== '');
		});
	}
	csvColumns.value = headers;
	csvRows.value = dataRows.map(r => normalizeRowLength(r, headers.length));
	// Try auto-map if we have a schema and headers
	if (selectedSchema.value && firstRowIsHeader.value) {
		autoMap();
	}
}

function normalizeRowLength(row, len) {
	const out = row.slice(0, len);
	while (out.length < len) out.push('');
	return out;
}

function sanitizeHeader(h) {
	return h.replace(/[\n\r\t]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function detectDelimiter(sample) {
	// Heuristic between comma, semicolon, and tab using counts outside quotes over first ~20 lines
	const maxLines = 20;
	let lineCount = 0;
	const counts = { ',': 0, ';': 0, '\t': 0 };
	let inQuotes = false;
	for (let i = 0; i < sample.length && lineCount < maxLines; i++) {
		const ch = sample[i];
		if (inQuotes) {
			if (ch === '"') {
				if (sample[i + 1] === '"') { i++; }
				else { inQuotes = false; }
			}
			continue;
		}
		if (ch === '"') { inQuotes = true; continue; }
		if (ch === '\n') { lineCount++; continue; }
		if (ch === ',') counts[',']++;
		else if (ch === ';') counts[';']++;
		else if (ch === '\t') counts['\t']++;
	}
	// Choose the delimiter with the highest count; fallback to comma
	let best = ',';
	let bestCount = counts[','];
	for (const key of [';', '\t']) {
		if (counts[key] > bestCount) { best = key; bestCount = counts[key]; }
	}
	return best === '\t' ? '\t' : best; // normalize
}

function resolveDelimiter(text) {
	if (delimiterMode.value === 'guess') return detectDelimiter(text);
	if (delimiterMode.value === 'tab') return '\t';
	return delimiterMode.value; // ',' or ';'
}

function parseCsv(text) {
	const delimiter = resolveDelimiter(text);
	const rows = [];
	let field = '';
	let row = [];
	let inQuotes = false;
	for (let i = 0; i < text.length; i++) {
		const c = text[i];
		if (inQuotes) {
			if (c === '"') {
				if (text[i + 1] === '"') { field += '"'; i++; }
				else { inQuotes = false; }
			} else {
				field += c;
			}
		} else {
			if (c === '"') { inQuotes = true; }
			else if (c === delimiter) { row.push(field); field = ''; }
			else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
			else if (c === '\r') {
				if (text[i + 1] === '\n') { continue; }
				row.push(field); rows.push(row); row = []; field = '';
			}
			else { field += c; }
		}
	}
	// last field
	row.push(field);
	rows.push(row);
	return { delimiter, rows: rows.filter(r => r.length && !(r.length === 1 && r[0] === '')) };
}

function autoMap() {
	if (!selectedSchema.value || !csvColumns.value.length) return;
	const fields = selectedSchema.value.fields || [];
	const byExact = new Map();
	const byLabel = new Map();
	const byField = new Map();
	const seps = [' ', '.', '>', '›', '/', ':', '-'];
	for (const f of fields) {
		const baseLabel = f.label || f.field;
		// Base exact matches
		byExact.set(normalizeKey(baseLabel), f.field);
		byExact.set(normalizeKey(f.field), f.field);
		if (f.fieldtype !== 'relationship') {
			byLabel.set(normalizeKey(baseLabel), f.field);
			byField.set(normalizeKey(f.field), f.field);
			continue;
		}
		// Relationship: prefer base -> id when header equals relationship label/name
		byLabel.set(normalizeKey(baseLabel), f.field);
		byField.set(normalizeKey(f.field), f.field);
		// Variants for id
		for (const sep of seps) {
			byLabel.set(normalizeKey(baseLabel + sep + 'id'), f.field);
			byField.set(normalizeKey(f.field + sep + 'id'), f.field);
		}
		// Subfields
		const rel = f.getRelationshipDetails?.();
		if (rel && Array.isArray(rel.oppositeSchemas)) {
			for (const oppSchema of rel.oppositeSchemas) {
				for (const sf of (oppSchema.fields || [])) {
					if (sf.field === 'id') continue;
					const subLabel = sf.label || sf.field;
					for (const sep of seps) {
						byLabel.set(normalizeKey(baseLabel + sep + subLabel), `${f.field}.${sf.field}`);
						byField.set(normalizeKey(f.field + sep + sf.field), `${f.field}.${sf.field}`);
					}
				}
			}
		}
	}
	const used = new Set();
	const mapping = {};
	for (let i = 0; i < csvColumns.value.length; i++) {
		const header = csvColumns.value[i];
		const key = normalizeKey(header);
		let match = byExact.get(key) || byLabel.get(key) || byField.get(key) || null;
		if (match && !used.has(match)) {
			mapping[i] = match;
			used.add(match);
		}
	}
	columnMapping.value = mapping;
}

function clearMap() {
	columnMapping.value = {};
}

function normalizeKey(s) {
	return String(s).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function toDataFieldKey(colName) {
    // Use plain path when safe, otherwise use bracket notation to preserve literal keys (dots/spaces)
    const safePathRegex = /^[A-Za-z_$][A-Za-z0-9_$]*(?:\.[A-Za-z_$][A-Za-z0-9_$]*)*$/;
    if (safePathRegex.test(colName)) return colName;
    const escaped = String(colName).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
    return "['" + escaped + "']";
}

const canImport = computed(() => {
	return !!selectedSchema.value && csvRows.value.length > 0 && Object.values(columnMapping.value).some(Boolean) && !!props.context?.group_id && !isImporting.value;
});

const importHint = computed(() => {
	if (!props.context?.group_id) return 'Missing group context';
	if (!selectedSchema.value) return 'Select a blueprint';
	if (!csvRows.value.length) return 'Load a CSV file first';
	if (!Object.values(columnMapping.value).some(Boolean)) return 'Map at least one column';
	return 'Import';
});

// Simulation popup state
const showSimPopup = ref(false);
const simulateGenerated = ref([]);
const simulateJsonPretty = computed(() => {
	try {
		return JSON.stringify(simulateGenerated.value, null, 2);
	} catch (_) {
		return String(simulateGenerated.value ?? '');
	}
});

// Count primary items vs children by comparing type_id to the selected schema
const simulateItemCount = computed(() => {
	if (!simulateGenerated.value?.length || !selectedSchema.value) return 0;
	const primaryTypeId = selectedSchema.value.id;
	return simulateGenerated.value.filter(item => item?.type_id === primaryTypeId).length;
});

const simulateChildrenCount = computed(() => {
	if (!simulateGenerated.value?.length || !selectedSchema.value) return 0;
	const primaryTypeId = selectedSchema.value.id;
	return simulateGenerated.value.filter(item => item?.type_id !== primaryTypeId).length;
});

function openSimulatePopup(generated) {
	simulateGenerated.value = Array.isArray(generated) ? generated : (generated ? [generated] : []);
	showSimPopup.value = true;
}

async function copySimJson() {
	const text = simulateJsonPretty.value || '';
	try {
		await navigator.clipboard.writeText(text);
		dxAlert('JSON copied to clipboard.', 'Copied');
	} catch (e) {
		try {
			const ta = document.createElement('textarea');
			ta.value = text;
			ta.style.position = 'fixed';
			ta.style.opacity = '0';
			document.body.appendChild(ta);
			ta.select();
			document.execCommand('copy');
			document.body.removeChild(ta);
			dxAlert('JSON copied to clipboard.', 'Copied');
		} catch (_) {
			dxAlert('Failed to copy JSON. Please copy manually.', 'Copy failed');
		}
	}
}

async function runImport(simulate = false, dumpPayload = false) {
	// Block only the primary import when locked; allow dropdown actions
	if (!importUnlocked.value && simulate !== true && dumpPayload !== true) {
		dxAlert('Please disable the safety switch to enable this import button.', 'Locked for safety');
		return;
	}
	if (!canImport.value) return;
	
	try {
		isImporting.value = true;
        const payloadRows = buildPayloadRows();
		if (dumpPayload === true) {
			dxAlert(`Dumped ${payloadRows.length} payload rows to console.`, 'Payload dumped');
			console.log("Payload rows:", payloadRows);
			return;
		}
		const response = await $http.post('/mote/import', {
			group_id: props.context.group_id,
			type_id: selectedSchema.value.id,
			simulate: simulate === true ? 1 : 0,
			data: payloadRows,
		});
		if (simulate === true) {
			console.log("Import simulation results:", response.data);
			openSimulatePopup(response.data?.generated);
		} else {
			dxAlert('Import created ' + (response.data?.created ?? payloadRows.length) + ' primary items for review.', 'Import started');
			emit('completed', { created: response.data?.created ?? payloadRows.length, schema: selectedSchema.value, group_id: props.context.group_id });
		}
	} catch (error) {
		console.error('Import error', error);
		if (error.response && error.response.data && (error.response.data.message || error.response.data.error)) {
			const msg = ('row' in error.response.data ? 'Row ' + error.response.data.row + ': ' : '') + ('field' in error.response.data ? 'Field ' + error.response.data.field + ': ' : '') + (error.response.data.message || error.response.data.error);
			dxAlert((simulate === true ? 'Simulate ' : '') + 'Import failed: ' + msg, 'Error');
		} else {
			dxAlert((simulate === true ? 'Simulate ' : '') + 'Import failed: ' + (error.message || 'Unknown error'), 'Error');
		}
	} finally {
		isImporting.value = false;
	}
}

function buildPayloadRows() {
	const rows = rowsAsObjects.value.map(rowObj => {
		const out = {};
		const fieldByName = new Map((selectedSchema.value?.fields || []).map(f => [f.field, f]));
		for (const [idxStr, target] of Object.entries(columnMapping.value)) {
			if (!target) continue;
			const idx = Number(idxStr);
			const colName = csvColumns.value[idx];
			const value = rowObj[colName];
			const targetStr = String(target);
			if (targetStr.includes('.')) {
				const [parentKey, subKey] = targetStr.split('.');
				if (!out[parentKey] || typeof out[parentKey] !== 'object' || Array.isArray(out[parentKey])) out[parentKey] = {};
				out[parentKey][subKey] = value;
			} else {
				const fObj = fieldByName.get(targetStr);
				if (fObj && fObj.fieldtype === 'relationship') {
					if (!out[targetStr] || typeof out[targetStr] !== 'object' || Array.isArray(out[targetStr])) out[targetStr] = {};
					out[targetStr]['id'] = value;
				} else {
					out[targetStr] = value;
				}
			}
		}
		return out;
	});

	// Clean empty relation objects if option is enabled
	if (ignoreEmptyRelations.value) {
		return rows.map(cleanEmptyRelations);
	}
	return rows;
}

/**
 * Check if an object represents an empty relation (empty object or all values are null/undefined/empty string)
 */
function isEmptyRelation(obj) {
	if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
	const keys = Object.keys(obj);
	if (keys.length === 0) return true;
	return keys.every(k => {
		const v = obj[k];
		return v === null || v === undefined || v === '';
	});
}

/**
 * Remove relation keys from a row if they are empty or contain only null/empty values
 */
function cleanEmptyRelations(row) {
	const cleaned = { ...row };
	for (const key of Object.keys(cleaned)) {
		const value = cleaned[key];
		if (value && typeof value === 'object' && !Array.isArray(value)) {
			if (isEmptyRelation(value)) {
				delete cleaned[key];
			}
		}
	}
	return cleaned;
}

// Duplicate mapping detection (warn when multiple columns map to same target path)
const duplicateMappingInfo = computed(() => {
	if (!selectedSchema.value) return [];
	const fieldByName = new Map((selectedSchema.value.fields || []).map(f => [f.field, f]));
	const byTarget = {};
	for (const [idxStr, target] of Object.entries(columnMapping.value)) {
		if (!target) continue;
		const idx = Number(idxStr);
		const header = csvColumns.value[idx];
		let canonical = String(target);
		if (!canonical.includes('.')) {
			const fObj = fieldByName.get(canonical);
			if (fObj && fObj.fieldtype === 'relationship') canonical = `${canonical}.id`;
		}
		if (!byTarget[canonical]) byTarget[canonical] = [];
		byTarget[canonical].push(header || `Column ${idx + 1}`);
	}
	return Object.entries(byTarget).filter(([, cols]) => cols.length > 1).map(([target, cols]) => ({ target, cols }));
});

// Context menu for preview grid headers to map columns
function onPreviewContextMenu(e) {
	if (e.target !== 'header') return;
    // Use stable column name (original header text) instead of dataField, which may use bracket notation
    const colName = e.column?.name || e.column?.caption || e.column?.dataField;
    if (!colName) return;
    const idx = csvColumns.value.indexOf(colName);
	if (idx === -1) return;
	const current = columnMapping.value[idx];
	const items = [];
	// Ignore option
	items.push({
		text: 'Ignore',
		icon: current ? undefined : 'check',
		onItemClick: () => {
			const updated = { ...columnMapping.value };
			delete updated[idx];
			columnMapping.value = updated;
		}
	});

	if (selectedSchema.value && Array.isArray(selectedSchema.value.fields)) {
		for (const f of selectedSchema.value.fields) {
			if (f.field === 'id') continue;
			if (f.fieldtype !== 'relationship') {
				// Simple field item
				items.push({
					text: f.label || f.field,
					icon: current === f.field ? 'check' : undefined,
					onItemClick: () => {
						const updated = { ...columnMapping.value };
						updated[idx] = f.field;
						columnMapping.value = updated;
					}
				});
				continue;
			}
			// Relationship field with submenu
			const relItems = [];
			// Map to ID (treat base relationship as id)
			relItems.push({
				text: 'id',
				icon: (current === f.field || current === f.field + '.id') ? 'check' : undefined,
				onItemClick: () => {
					const updated = { ...columnMapping.value };
					// Map to base field; builder will coerce to id
					updated[idx] = f.field;
					columnMapping.value = updated;
				}
			});
			const rel = f.getRelationshipDetails?.();
			if (rel && Array.isArray(rel.oppositeSchemas) && rel.oppositeSchemas.length > 0) {
				// If multiple opposite schemas, group by each schema label
				if (rel.oppositeSchemas.length > 1) {
					for (const opp of rel.oppositeSchemas) {
						const oppLabel = opp.label || opp.singular || opp.title || opp.type || opp.keyspec || 'Related';
						const oppItems = [];
						for (const sf of (opp.fields || [])) {
							if (sf.field === 'id') continue;
							const valueKey = f.field + '.' + sf.field;
							oppItems.push({
								text: sf.label || sf.field,
								icon: current === valueKey ? 'check' : undefined,
								onItemClick: () => {
									const updated = { ...columnMapping.value };
									updated[idx] = valueKey;
									columnMapping.value = updated;
								}
							});
						}
						if (oppItems.length) {
							relItems.push({ text: oppLabel, items: oppItems });
						}
					}
				} else {
					// Single opposite schema: list subfields directly
					const opp = rel.oppositeSchemas[0];
					for (const sf of (opp.fields || [])) {
						if (sf.field === 'id') continue;
						const valueKey = f.field + '.' + sf.field;
						relItems.push({
							text: sf.label || sf.field,
							icon: current === valueKey ? 'check' : undefined,
							onItemClick: () => {
								const updated = { ...columnMapping.value };
								updated[idx] = valueKey;
								columnMapping.value = updated;
							}
						});
					}
				}
			}
			items.push({ text: f.label || f.field, items: relItems });
		}
	}
	e.items = items;
}
</script>

<style lang="scss">
.import-wizard {
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.step-card {
	background: var(--dx-color-surface, #fff);
	border: 1px solid var(--dx-color-border, rgba(0, 0, 0, 0.12));
	border-radius: 8px;
	padding: 12px;
}

.step-card.step-complete {
	border-color: rgba(56, 142, 60, 0.35);
	background: linear-gradient(0deg, rgba(76, 175, 80, 0.06), rgba(76, 175, 80, 0.06)), var(--dx-color-surface, #fff);
}

.step-header {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 8px;
}

.step-number {
	width: 24px;
	height: 24px;
	border-radius: 12px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	background: var(--dx-color-accent, #3f51b5);
	color: #fff;
	font-weight: 600;
	font-size: 12px;
    padding-top: 1.5px;
}

.step-title {
	font-weight: 600;
}
.import-wizard .mapping-tree .dx-treeview-node-container .dx-state-selected,
.import-wizard .mapping-tree .dx-treeview-item.dx-state-hover,
.import-wizard .mapping-tree .dx-treeview-item.dx-state-focused {
	background: transparent !important;
	color: inherit !important;
}


.import-locked .dx-dropdownbutton .dx-dropdownbutton-action.dx-button-mode-contained.dx-button-success,
.import-locked .dx-dropdownbutton .dx-dropdownbutton-action.dx-button-mode-contained.dx-button-success .dx-button-content {
	opacity: 0.5;
	transition: opacity 120ms ease;
}
</style>